import { Children, cloneElement, isValidElement, useId } from 'react'
import InfoHint from './InfoHint'
import './FormRow.css'

/**
 * Label + control pair, with the field-level validation message underneath.
 * `children` is called with the id so the label stays wired to the control.
 *
 * @param {string} [className]  layout only — `form-narrow` for a control that
 *   should not stretch to the sheet's full width, `form-span` inside FormCols.
 * @param {string} [hint]  guidance about the field. ALWAYS behind an "i" beside
 *   the label — never a line under the control.
 *
 *   Hints used to print themselves under the field whenever they were short
 *   enough, and a form of ten fields then carried ten lines of guidance nobody
 *   reads twice: the fields stopped looking like fields. The threshold also made
 *   the same form look like two designs, some rows captioned and some not. One
 *   rule instead — the label says what the field is, the "i" says the rest — and
 *   it is the rule the settings cards, the drawer titles and the page headers
 *   already follow.
 *
 *   The line under the control now belongs to the ERROR alone, so something
 *   appearing there always means something is wrong.
 */
export default function FormRow({ label, error, hint, className = '', children }) {
  const id = useId()
  const errorId = `${id}-error`

  /*
   * A LABEL POINTING AT NOTHING IS NOT A LABEL.
   *
   * The render-prop form above wires `htmlFor` to the control because the caller passes the
   * id on. A caller who writes the control as an ORDINARY CHILD — `<FormRow label="…">
   * <TextField … /></FormRow>` — got a `<label htmlFor={id}>` whose id no element carried,
   * so the field had no accessible name by any route: no `aria-label`, no `placeholder`, no
   * wrapping label, nothing. Twenty-one rows across five screens were written that way,
   * most of the learner's own forms among them — the profile it forces a gated user to
   * complete, the password page, and the support form.
   *
   * So the id is bound here instead of being the caller's job to remember. A control that
   * brought its own id keeps it, and the label follows it rather than overriding it.
   */
  const single = typeof children !== 'function' && Children.count(children) === 1
    ? Children.only(children)
    : null
  const bindable = isValidElement(single) ? single : null
  const controlId = bindable?.props?.id ?? id

  let control
  if (typeof children === 'function') {
    control = children(id, error ? errorId : undefined)
  } else if (bindable && !bindable.props.id) {
    control = cloneElement(bindable, {
      id,
      ...(error ? { 'aria-describedby': errorId } : null),
    })
  } else {
    control = children
  }

  return (
    <div className={`form-row ${className}`.trim()}>
      <label className="form-row__label" htmlFor={controlId}>
        <bdi>{label}</bdi>
        {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
      </label>
      <div className="form-row__control">{control}</div>
      {error && (
        <p className="form-row__error" id={errorId} role="alert">
          <bdi>{error}</bdi>
        </p>
      )}
    </div>
  )
}
